import { useEffect, useMemo, useState } from "react";
import {
    Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend, Bar, BarChart,
} from "recharts";
import {
    CurrencyDollar, Receipt, Money, CreditCard, Calculator, Plus, Trash, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api, authHeaders } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import PageHeader from "@/admin/components/PageHeader";
import StatCard from "@/admin/components/StatCard";

const PRESETS = [
    { id: "today", label: "Hoy", days: 1 },
    { id: "week", label: "Últimos 7", days: 7 },
    { id: "month", label: "Últimos 30", days: 30 },
    { id: "ytd", label: "Año actual", days: -1 },
];

const EXPENSE_CATEGORIES = [
    "inventario", "sueldos", "renta", "luz", "agua",
    "internet", "marketing", "mantenimiento", "otros",
];

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

function startOfYear() {
    return `${new Date().getFullYear()}-01-01`;
}

export default function Accounting() {
    const { token } = useAdmin();
    const [from, setFrom] = useState(daysAgo(6));
    const [to, setTo] = useState(todayISO());
    const [data, setData] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [newExp, setNewExp] = useState({
        category: "inventario", amount: "", description: "", date: todayISO(),
    });
    const [busy, setBusy] = useState(false);

    const load = () => {
        const q = `date_from=${from}&date_to=${to}`;
        api.get(`/admin/accounting?${q}`, { headers: authHeaders() })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Error al cargar"));
        api.get(`/admin/expenses?${q}`, { headers: authHeaders() })
            .then((r) => setExpenses(r.data.expenses))
            .catch(() => {});
    };

    useEffect(() => { load(); }, [from, to]);  // eslint-disable-line

    const setPreset = (p) => {
        if (p.id === "today") { setFrom(todayISO()); setTo(todayISO()); }
        else if (p.id === "ytd") { setFrom(startOfYear()); setTo(todayISO()); }
        else { setFrom(daysAgo(p.days - 1)); setTo(todayISO()); }
    };

    const addExpense = async () => {
        const amt = parseFloat(newExp.amount);
        if (!(amt > 0) || !newExp.category) {
            toast.error("Categoría y monto requeridos");
            return;
        }
        setBusy(true);
        try {
            await api.post("/admin/expenses", {
                category: newExp.category,
                amount: amt,
                description: newExp.description,
                date: newExp.date || todayISO(),
            }, { headers: authHeaders() });
            toast.success("Gasto registrado");
            setNewExp({ category: "inventario", amount: "", description: "", date: todayISO() });
            setAddOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setBusy(false);
        }
    };

    const removeExpense = async (id) => {
        if (!window.confirm("¿Eliminar este gasto?")) return;
        try {
            await api.delete(`/admin/expenses/${id}`, { headers: authHeaders() });
            toast.success("Eliminado");
            load();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const csvUrl = useMemo(() => {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return `${base}/api/admin/accounting.csv?date_from=${from}&date_to=${to}&token=${encodeURIComponent(token || "")}`;
    }, [from, to, token]);

    const t = data?.totals || {};
    const net = (t.revenue || 0) - (t.expenses || 0);

    return (
        <div data-testid="admin-accounting">
            <PageHeader
                kicker="Finanzas"
                title="Contabilidad"
                action={
                    <a
                        data-testid="accounting-csv"
                        href={csvUrl}
                        download
                        className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-4 py-2 text-xs font-bold uppercase tracking-widest hover:opacity-90"
                    >
                        <DownloadSimple size={14} weight="bold" /> CSV
                    </a>
                }
            />

            {/* Date range */}
            <Card className="p-4 mb-6">
                <div className="flex flex-wrap items-end gap-3" data-testid="acc-date-range">
                    <div className="flex gap-2 flex-wrap">
                        {PRESETS.map((p) => (
                            <button
                                key={p.id}
                                data-testid={`acc-preset-${p.id}`}
                                onClick={() => setPreset(p)}
                                className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border-2 border-border bg-white hover:border-foreground"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-end gap-2">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Desde
                            </label>
                            <Input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                data-testid="acc-from"
                                className="h-9 w-40"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Hasta
                            </label>
                            <Input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                data-testid="acc-to"
                                className="h-9 w-40"
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="acc-kpis">
                <StatCard icon={CurrencyDollar} kicker="Ventas brutas" value={`$${(t.revenue || 0).toFixed(2)}`} />
                <StatCard icon={Receipt} kicker="Órdenes" value={t.orders || 0} />
                <StatCard icon={Calculator} kicker="Ticket prom." value={`$${(t.avg_ticket || 0).toFixed(2)}`} />
                <StatCard icon={Calculator} kicker="Impuesto (Sales Tax)" value={`$${(t.tax || 0).toFixed(2)}`} />
                <StatCard icon={Money} kicker="Gastos" value={`$${(t.expenses || 0).toFixed(2)}`} />
                <StatCard
                    icon={CurrencyDollar}
                    kicker="Ganancia neta"
                    value={`$${net.toFixed(2)}`}
                    className={net >= 0 ? "" : "border-destructive"}
                />
            </div>

            {/* By payment method + Tax */}
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Por método de pago
                    </p>
                    {Object.keys(data?.by_method || {}).length === 0 ? (
                        <p className="text-sm text-muted-foreground">Sin datos en este rango.</p>
                    ) : (
                        <div className="space-y-2">
                            {Object.entries(data?.by_method || {}).map(([m, v]) => (
                                <PayRow key={m} method={m} count={v.count} total={v.total} />
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Reporte de Impuestos · Sales Tax
                    </p>
                    <p className="font-display font-black text-3xl">
                        ${(t.tax || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Impuesto recolectado entre {data?.range?.from} y {data?.range?.to}
                    </p>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-muted rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Base imponible</p>
                            <p className="font-display font-black text-lg">${(t.subtotal || 0).toFixed(2)}</p>
                        </div>
                        <div className="bg-muted rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Fees envío</p>
                            <p className="font-display font-black text-lg">${(t.delivery_fees || 0).toFixed(2)}</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Daily chart */}
            <Card className="p-5 mb-6">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                    Ventas por día (cash vs tarjeta)
                </p>
                <div className="h-72" data-testid="acc-chart">
                    {data?.by_day?.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.by_day}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="cash" stackId="a" name="Efectivo" fill="#10b981" />
                                <Bar dataKey="card" stackId="a" name="Tarjeta (terminal)" fill="#3b82f6" />
                                <Bar dataKey="stripe" stackId="a" name="Stripe" fill="#8b5cf6" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="text-sm text-muted-foreground">Sin ventas en este rango.</p>
                    )}
                </div>
            </Card>

            {/* Expenses */}
            <Card className="p-5">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                        Gastos registrados
                    </p>
                    <Button
                        data-testid="exp-add-btn"
                        size="sm"
                        onClick={() => setAddOpen(true)}
                        className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                    >
                        <Plus size={12} weight="bold" className="mr-1" /> Registrar gasto
                    </Button>
                </div>

                {expenses.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                        Sin gastos en este rango. Registra renta, sueldos, inventario, etc. para ver tu ganancia neta.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="exp-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-3 py-2">Fecha</th>
                                    <th className="px-3 py-2">Categoría</th>
                                    <th className="px-3 py-2">Descripción</th>
                                    <th className="px-3 py-2 text-right">Monto</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map((e) => (
                                    <tr key={e.id} className="border-t border-border" data-testid={`exp-row-${e.id}`}>
                                        <td className="px-3 py-2">{e.date}</td>
                                        <td className="px-3 py-2 capitalize">{e.category}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{e.description || "—"}</td>
                                        <td className="px-3 py-2 text-right font-bold">${e.amount.toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                data-testid={`exp-del-${e.id}`}
                                                onClick={() => removeExpense(e.id)}
                                                className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive"
                                            >
                                                <Trash size={12} weight="bold" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Add expense dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent data-testid="exp-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            Registrar gasto
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Categoría
                            </label>
                            <select
                                data-testid="exp-cat"
                                value={newExp.category}
                                onChange={(e) => setNewExp({ ...newExp, category: e.target.value })}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            >
                                {EXPENSE_CATEGORIES.map((c) => (
                                    <option key={c} value={c}>
                                        {c.charAt(0).toUpperCase() + c.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <Input
                            data-testid="exp-amount"
                            type="number"
                            step="0.01"
                            placeholder="Monto $"
                            value={newExp.amount}
                            onChange={(e) => setNewExp({ ...newExp, amount: e.target.value })}
                        />
                        <Input
                            data-testid="exp-desc"
                            placeholder="Descripción (opcional)"
                            value={newExp.description}
                            onChange={(e) => setNewExp({ ...newExp, description: e.target.value })}
                        />
                        <Input
                            data-testid="exp-date"
                            type="date"
                            value={newExp.date}
                            onChange={(e) => setNewExp({ ...newExp, date: e.target.value })}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">
                            Cancelar
                        </Button>
                        <Button
                            data-testid="exp-save"
                            disabled={busy}
                            onClick={addExpense}
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const METHOD_META = {
    cash:   { label: "Efectivo",          icon: Money,      color: "text-emerald-600" },
    card:   { label: "Tarjeta (terminal)", icon: CreditCard, color: "text-blue-600" },
    stripe: { label: "Stripe",            icon: CreditCard, color: "text-violet-600" },
    online: { label: "Online (legado)",   icon: CreditCard, color: "text-foreground" },
};

function PayRow({ method, count, total }) {
    const m = METHOD_META[method] || { label: method, icon: CreditCard, color: "text-foreground" };
    const I = m.icon;
    return (
        <div className="flex items-center justify-between p-3 rounded-xl bg-muted">
            <div className="flex items-center gap-3">
                <I size={20} weight="fill" className={m.color} />
                <div>
                    <p className="font-bold text-sm">{m.label}</p>
                    <p className="text-xs text-muted-foreground">{count} {count === 1 ? "orden" : "órdenes"}</p>
                </div>
            </div>
            <p className="font-display font-black text-xl">${total.toFixed(2)}</p>
        </div>
    );
}
